import { Log } from '@lightningjs/sdk';
import { EpgApi } from './EpgApi';
import { ChannelModel } from './EpgApi.types';
import { BaseCachedApi } from './BaseCachedApi';
import { Environment } from '../config/environment';
import { Backend } from './Backend';
import { IChannelRightsV5, IChannelV5 } from './models/AndromeTV.types';
import { StreamType } from './models/Shared.types';
import { SettingsKeys } from './models/TranslationKeys';

export class CachedChannelApi extends BaseCachedApi {
  protected override _cache!: ChannelModel[];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(backend: Backend) {
    super();
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this.updateExpirations(120000, 120000);
  }

  channels(backend: Backend): Promise<ChannelModel[]> {
    const promise = this.checkCache(backend);

    return promise.then(() => {
      //todo sort?
      return this._cache;
    }) as Promise<ChannelModel[]>;
  }

  validChannels(
    backend: Backend,
    replay: boolean
  ): Promise<{ allChannels: ChannelModel[]; validChannels: ChannelModel[] }> {
    const requiredOttRights = replay
      ? (Environment.atv_replay_ott_rights as StreamType[])
      : (Environment.atv_ott_rights as StreamType[]);
    const catalogId = backend.deviceProfile?.profile.catalog;
    const prom = this.checkCache(backend);

    return prom.then(() => {
      const channels = this._cache.filter(
        channel => !channel.catalogs || channel.catalogs.some(catalog => catalog.catalogId === catalogId)
      );

      const validChannels = channels.filter(channel => {
        if (channel.adult) return false;
        if (!channel.enabled || channel.audioOnly) {
          return false;
        }

        if (backend.isAuthorized()) {
          return channel.hasOneofMaxOttRights(requiredOttRights);
        }

        return channel.hasOneOfCustomerRights(requiredOttRights);
      });

      return {
        allChannels: channels,
        validChannels,
      };
    });
  }

  override fillCache(backend: Backend) {
    const channelsProm = this._getChannels(backend);
    const rightsProm = this._getRights(backend);
    this.clearCache();

    return Promise.all([channelsProm, rightsProm]).then(([channels, rights]) => {
      this._cache = (<IChannelV5[]>channels).map(channel => {
        const ch = new ChannelModel(channel);
        rights.some(right => {
          if (right.channelId === ch.channelId) {
            ch.rights = right;
            return true;
          }
          return false;
        });
        return ch;
      });
    });
  }

  /**
   * @private
   */
  _getChannels(backend: Backend): Promise<IChannelV5[]> {
    return new Promise(resolve => {
      EpgApi.channels(backend)
        .then(result => {
          resolve(result);
        })
        .catch(error => {
          Log.error(error);
          resolve([]);
        });
    });
  }

  /**
   * @private
   */
  _getRights(backend: Backend): Promise<IChannelRightsV5[]> {
    return new Promise(resolve => {
      backend.cachedChannelRightsApi
        .getOttChannelRights(backend)
        .then(result => resolve(result))
        .catch(error => {
          Log.error(error);
          resolve([]);
        });
    });
  }
}
